import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import InstructorAssignmentsSection from "./InstructorAssignmentsSection";
import InstructorProfileSection from "./InstructorProfileSection";
import { useInstructorForm } from "../useInstructorForm";

type Props = {
  open: boolean;
  editingId: number | null;
  isArabic: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

function ModalHeader({ editingId, isArabic, onClose }: Pick<Props, "editingId" | "isArabic" | "onClose">) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 pb-4 pt-5 dark:border-white/10 sm:px-7 sm:pt-7">
      <div>
        <h4 className="text-xl font-extrabold text-navy-700 dark:text-white">
          {editingId
            ? isArabic
              ? "تعديل بيانات المدرس"
              : "Edit instructor"
            : isArabic
              ? "إضافة مدرس جديد"
              : "Add new instructor"}
        </h4>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {editingId
            ? isArabic
              ? `رقم السجل: #${editingId}`
              : `Record ID: #${editingId}`
            : isArabic
              ? "أدخل بيانات الحساب والتعيينات الأكاديمية ثم احفظ."
              : "Add account details and academic assignments, then save."}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={isArabic ? "إغلاق" : "Close"}
        className="rounded-xl bg-gray-100 px-3 py-2 text-xl leading-none text-gray-600 transition hover:bg-gray-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
      >
        ×
      </button>
    </div>
  );
}

function ModalFooter({ editingId, isArabic, saving, onClose, onSubmit }: {
  editingId: number | null;
  isArabic: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className={`flex shrink-0 gap-2 border-t border-gray-200 px-5 pb-5 pt-4 dark:border-white/10 sm:px-7 sm:pb-7 ${isArabic ? "flex-row-reverse" : ""}`}>
      <button
        type="button"
        disabled={saving}
        onClick={onSubmit}
        className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving
          ? isArabic
            ? "جارٍ الحفظ..."
            : "Saving..."
          : editingId
            ? isArabic
              ? "حفظ التعديلات"
              : "Save changes"
            : isArabic
              ? "إنشاء المدرس"
              : "Create instructor"}
      </button>
      <button
        type="button"
        onClick={onClose}
        className="rounded-xl bg-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-300 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
      >
        {isArabic ? "إغلاق" : "Close"}
      </button>
    </div>
  );
}

export default function InstructorFormModal({ open, editingId, isArabic, onClose, onSaved }: Props) {
  const controller = useInstructorForm({ open, editingId, isArabic, onClose, onSaved });
  if (!open) return null;

  const body: ReactNode = controller.loadingDetail ? (
    <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
      {isArabic ? "جارٍ تحميل بيانات المدرس..." : "Loading instructor..."}
    </p>
  ) : (
    <>
      <InstructorProfileSection
        form={controller.form}
        isArabic={isArabic}
        uploadingImage={controller.uploadingImage}
        onChange={controller.updateForm}
        onImageChange={controller.handleImageFileChange}
      />
      <InstructorAssignmentsSection
        form={controller.form}
        isArabic={isArabic}
        controller={controller}
        onChange={controller.updateForm}
      />
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center overflow-y-auto bg-black/55 p-4 backdrop-blur-[3px]"
      onClick={onClose}
    >
      <div
        className="my-auto flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl dark:bg-navy-800"
        onClick={(event) => event.stopPropagation()}
        dir={isArabic ? "rtl" : "ltr"}
        role="dialog"
        aria-modal="true"
      >
        <ModalHeader editingId={editingId} isArabic={isArabic} onClose={onClose} />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-7">{body}</div>
        {!controller.loadingDetail ? (
          <ModalFooter
            editingId={editingId}
            isArabic={isArabic}
            saving={controller.saving}
            onClose={onClose}
            onSubmit={() => void controller.handleSubmit()}
          />
        ) : null}
      </div>
    </div>,
    document.body
  );
}
